import { useMemo, useState } from "react";
import { usePrava } from "@/api/hooks/useAuth";
import { VETA } from "@/lib/prava";
import { Combine, Copy, Pencil, Star, Tag, Trash2 } from "lucide-react";
import { AliasDialog } from "@/components/cennik/AliasDialog";
import { StavZnaky } from "@/components/cennik/StavZnaky";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useCennikMaterialy,
  useKovaniaPopularne,
  useUpdateMaterial,
} from "@/api/hooks/useCennik";
import { useDebounce } from "@/lib/useDebounce";
import { formatEur, materialPrimaryId, menoKovania } from "@/lib/format";
import { PravyNazov } from "@/components/cennik/PravyNazov";
import { formatKategoria } from "@/lib/kategorie";
import { MaterialSelect } from "@/components/slot/MaterialSelect";
import {
  KovanieSetDialog,
  parseSetComponents,
  popisSetu,
} from "@/components/cennik/KovanieSetDialog";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { SetZnak } from "@/components/cennik/SetZnak";
import type { Material } from "@/api/schemas";

/**
 * Stránka Kovania — kurátorský výber pre dielňu:
 *
 * - Sety kovaní (Spojiť kovania) — jedna položka z viacerých komponentov,
 *   práca sa účtuje raz; úprava a mazanie setov.
 * - Obľúbené / najčastejšie používané kovania zoskupené podľa kategórií
 *   (Závesy, Nohy, Výsuvy…) — zamestnanec ich pri pridávaní kovania do
 *   zákazky vyberá bez znalosti ID.
 * - Najpoužívanejšie kovania zo zákaziek — kandidáti na obľúbené.
 */

/** Kľúč skupiny bez kategórie kovania — v zozname obľúbených je posledná. */
const OSTATNE = "__ostatne__";

export function KovaniaPage(): JSX.Element {
  const prava = usePrava();
  const [setDialogOpen, setSetDialogOpen] = useState(false);
  const [editSet, setEditSet] = useState<Material | null>(null);
  const [duplikatSet, setDuplikatSet] = useState<Material | null>(null);
  // Alias dialóg — otvára sa po pridaní hviezdičky a cez Tag tlačidlo
  const [aliasTarget, setAliasTarget] = useState<Material | null>(null);
  // Označené kovanie v paneli Pridať do obľúbených (pás Vybrané s eshopom)
  const [oznacene, setOznacene] = useState<string | null>(null);
  const updateMutation = useUpdateMaterial();

  // Sety kovaní — serverové vyhľadávanie (setov sú tisíce — Demos K-sety)
  const [setyFilter, setSetyFilter] = useState("");
  const setyFilterDeb = useDebounce(setyFilter, 300);
  // Sety a obľúbené sú zoznamy usera, nie výber z cenníka — bez `vyber`,
  // aby sa dali spravovať aj tie, ktoré by picker už neponúkol
  const setyQuery = useCennikMaterialy({
    sekcia: "kovanie",
    je_set: true,
    // len ručne vytvorené sety (Spojiť kovania) — dodávateľské
    // K-sety (Demos) sem nepatria
    set_rucny: true,
    search: setyFilterDeb || undefined,
    per_page: 50,
  });
  const setyZobrazene = useMemo(
    () => setyQuery.data?.data ?? [],
    [setyQuery.data],
  );
  const setyTotal = setyQuery.data?.pagination?.total ?? 0;

  // Všetky obľúbené kovania — grouping klientsky podľa skupín
  const oblubeneQuery = useCennikMaterialy({
    sekcia: "kovanie",
    je_oblubene: true,
    per_page: 300,
  });
  const oblubene = useMemo(
    () => oblubeneQuery.data?.data ?? [],
    [oblubeneQuery.data],
  );

  // Najpoužívanejšie kovania zo zákaziek
  const popularneQuery = useKovaniaPopularne();
  const popularne = popularneQuery.data?.data ?? [];

  // Obľúbené po skupinách — skupinu kategórie určuje server
  // (`Material.skupina`), takže Knopka je pod Úchytkami a Tlmiče majú
  // vlastnú skupinu. Abecedne, bez skupiny na koniec.
  const skupiny = useMemo(() => {
    const map = new Map<string, Material[]>();
    for (const m of oblubene) {
      const key = m.skupina ?? OSTATNE;
      const arr = map.get(key) ?? [];
      arr.push(m);
      map.set(key, arr);
    }
    return [...map.entries()]
      .map(([k, items]) => ({
        key: k,
        label: k === OSTATNE ? "Ostatné" : formatKategoria(k),
        items: items.sort((a, b) => a.nazov.localeCompare(b.nazov, "sk")),
      }))
      .sort((a, b) =>
        a.key === OSTATNE
          ? 1
          : b.key === OSTATNE
            ? -1
            : a.label.localeCompare(b.label, "sk", { numeric: true }),
      );
  }, [oblubene]);

  async function toggleOblubene(m: Material, value: boolean): Promise<void> {
    try {
      await updateMutation.mutateAsync({
        id: m.id,
        data: { je_oblubene: value },
      });
      toast({
        title: value ? "Pridané do obľúbených" : "Odobrané z obľúbených",
        description: m.nazov,
      });
      // po pridaní hviezdičky ponúkni alias (hľadateľné pomenovanie)
      if (value) setAliasTarget(m);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  async function zmazatSet(m: Material): Promise<void> {
    if (!window.confirm(`Zmazať set „${m.nazov}“?`)) return;
    try {
      await updateMutation.mutateAsync({
        id: m.id,
        data: { je_aktivny: false },
      });
      toast({ title: "Set zmazaný", description: m.nazov });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri mazaní setu",
        description: extractErrorMessage(err),
      });
    }
  }

  function otvorEditSetu(m: Material): void {
    setEditSet(m);
    setDuplikatSet(null);
    setSetDialogOpen(true);
  }

  function otvorDuplikatSetu(m: Material): void {
    setEditSet(null);
    setDuplikatSet(m);
    setSetDialogOpen(true);
  }

  // Obľúbené a sety sú cenník výroby — obchodník ich len vidí (KOLO 231)
  if (!prava.cennik_vyroby) {
    return (
      <div className="space-y-6">
        <p className="text-xs text-muted-foreground">{VETA.cennikVyroby}</p>
        <fieldset disabled className="min-w-0 space-y-6">
          {obsah()}
        </fieldset>
      </div>
    );
  }
  return obsah();

  function obsah(): JSX.Element {
  return (
    <div className="space-y-6">
      {/* Na mobile nadpis s popisom nad tlačidlom (KOLO 212) — vedľa seba
          sa popis lámal po slove do úzkeho stĺpca. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-4">
          <h1 className="text-3xl">Kovania</h1>
          <p className="text-sm text-muted-foreground">
            Obľúbené kovania a sety — výber podľa kategórií pre dielňu
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setEditSet(null);
            setSetDialogOpen(true);
          }}
          title="Spoj viac kovaní do jednej položky — cena je súčet, práca sa účtuje raz"
        >
          <Combine className="h-4 w-4" />
          Spojiť kovania
        </Button>
      </div>

      {/* Sety kovaní — úprava a mazanie */}
      <div className="space-y-2 rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Sety kovaní ({setyTotal})
          </h2>
          <Input
            value={setyFilter}
            onChange={(e) => setSetyFilter(e.target.value)}
            placeholder="Filtrovať sety…"
            aria-label="Filtrovať sety"
            className="h-7 w-56 text-xs"
          />
        </div>
        {setyQuery.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : setyZobrazene.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Zatiaľ žiadne sety — vytvor ich tlačidlom „Spojiť kovania“.
          </p>
        ) : (
          <ul className="max-h-80 divide-y overflow-y-auto rounded-md border">
            {setyZobrazene.map((m) => {
              const komponenty = popisSetu(parseSetComponents(m));
              return (
                <li
                  key={m.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="truncate">
                      <SetZnak className="mr-1.5 inline-block align-middle" />
                      {menoKovania(m).hlavne}
                    </div>
                    <PravyNazov material={m} />
                    {komponenty ? (
                      <div className="truncate text-xs text-muted-foreground">
                        {komponenty}
                      </div>
                    ) : null}
                    <div className="font-mono text-xs text-muted-foreground">
                      {formatEur(m.nakup_cena)}/{m.mj}
                      {m.kategoria ? ` · ${formatKategoria(m.kategoria)}` : ""}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      aria-label={`Duplikovať set ${m.nazov}`}
                      title="Duplikovať — nový set s rovnakými komponentmi"
                      onClick={() => otvorDuplikatSetu(m)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      aria-label={`Upraviť set ${m.nazov}`}
                      title="Upraviť set kovaní"
                      onClick={() => otvorEditSetu(m)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      aria-label={`Zmazať set ${m.nazov}`}
                      title="Zmazať set kovaní"
                      onClick={() => void zmazatSet(m)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Pridať do obľúbených — ten istý výber z cenníka ako v zákazke
          (KOLO 202): hľadanie, Kategória s počtami, Aj iní dodávatelia;
          ★ na konci riadku pridá / odoberie, dvojklik či Enter pridá.
          Je to výber z cenníka, takže rozsah pickera (`vyber`) — rodič
          skupiny vráti aj podskupiny (Úchytky aj s Knopkou, KOLO 166). */}
      <div className="space-y-2 rounded-lg border bg-card p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Pridať do obľúbených
        </h2>
        <MaterialSelect
          value={oznacene}
          onChange={(id) => setOznacene(id)}
          onPotvrd={(m) => {
            if (!m.je_oblubene) void toggleOblubene(m as Material, true);
          }}
          sekcia="kovanie"
          // Zoznam až po kliknutí do hľadania alebo výbere kategórie —
          // stránka patrí obľúbeným, nie celému cenníku
          kompaktny
          placeholder="Vyhľadať kovanie (názov, kód, dekor)…"
          akciaRiadku={(m) => (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              aria-label={
                m.je_oblubene
                  ? `Odobrať z obľúbených ${m.nazov}`
                  : `Pridať do obľúbených ${m.nazov}`
              }
              title="Pridať / odobrať z obľúbených"
              onClick={() => void toggleOblubene(m as Material, !m.je_oblubene)}
            >
              <Star
                className={
                  "h-4 w-4 " +
                  (m.je_oblubene ? "fill-amber-400 text-amber-400" : "text-muted-foreground")
                }
              />
            </Button>
          )}
        />
      </div>

      {/* Najpoužívanejšie kovania zo zákaziek — kandidáti na obľúbené */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Najpoužívanejšie v zákazkách</h2>
        {popularneQuery.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : popularne.length === 0 ? (
          <p className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
            Zatiaľ žiadne použitie v zákazkách.
          </p>
        ) : (
          <div className="rounded-md border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Názov</TableHead>
                  <TableHead className="w-[180px]">Kategória</TableHead>
                  <TableHead className="w-[180px] text-right">
                    Použitie
                  </TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {popularne.map(({ material: m, pocet_ks, pocet_dielcov }) => (
                  <TableRow key={m.id}>
                    <TableCell>
                      {m.je_set ? <SetZnak className="mr-1.5 inline-block align-middle" /> : null}
                      {menoKovania(m).hlavne}
                      <PravyNazov material={m} />
                    </TableCell>
                    <TableCell className="text-xs">
                      {m.kategoria ? formatKategoria(m.kategoria) : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {pocet_ks}× ks · {pocet_dielcov}× dielec
                    </TableCell>
                    <TableCell>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        aria-label={
                          m.je_oblubene
                            ? `Odobrať z obľúbených ${m.nazov}`
                            : `Pridať do obľúbených ${m.nazov}`
                        }
                        title="Pridať / odobrať z obľúbených"
                        onClick={() => void toggleOblubene(m, !m.je_oblubene)}
                      >
                        <Star
                          className={
                            "h-4 w-4 " +
                            (m.je_oblubene
                              ? "fill-amber-400 text-amber-400"
                              : "text-muted-foreground")
                          }
                        />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {/* Obľúbené podľa kategórií */}
      {oblubeneQuery.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : skupiny.length === 0 ? (
        <p className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">
          Zatiaľ žiadne obľúbené kovania. Vyhľadaj kovanie hore a označ ho
          hviezdičkou — zamestnanec ho potom uvidí pri pridávaní kovania do
          zákazky vo výbere podľa kategórií.
        </p>
      ) : (
        skupiny.map((sk) => (
          <section key={sk.key} className="space-y-2">
            <h2 className="text-lg font-semibold">
              {sk.label}{" "}
              <span className="text-sm font-normal text-muted-foreground">
                ({sk.items.length})
              </span>
            </h2>
            <div className="rounded-md border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Názov</TableHead>
                    <TableHead className="w-[160px]">ID</TableHead>
                    <TableHead className="w-[140px]">Kategória</TableHead>
                    <TableHead className="w-[110px] text-right">
                      Cena/MJ
                    </TableHead>
                    <TableHead className="w-[50px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sk.items.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>
                        {m.je_set ? <SetZnak className="mr-1.5 inline-block align-middle" /> : null}
                        {/* Alias je hlavný nápis, pravý názov pod ním (KOLO 176) */}
                        {menoKovania(m).hlavne}
                        {/* Obľúbený výpredaj výber kovania neukáže (KOLO
                            166) — štítok povie prečo */}
                        <StavZnaky material={m} />
                        <PravyNazov material={m} />
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {materialPrimaryId(m) ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {m.kategoria ? formatKategoria(m.kategoria) : "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {formatEur(m.nakup_cena)}/{m.mj}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-0.5">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            aria-label={`Alias ${m.nazov}`}
                            title={
                              m.alias
                                ? `Upraviť alias: ${m.alias}`
                                : "Pridať alias (hľadateľné pomenovanie)"
                            }
                            onClick={() => setAliasTarget(m)}
                          >
                            <Tag
                              className={
                                "h-4 w-4 " +
                                (m.alias
                                  ? "text-primary"
                                  : "text-muted-foreground")
                              }
                            />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            aria-label={`Odobrať z obľúbených ${m.nazov}`}
                            title="Odobrať z obľúbených"
                            onClick={() => void toggleOblubene(m, false)}
                          >
                            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        ))
      )}

      <KovanieSetDialog
        open={setDialogOpen}
        onOpenChange={(o) => {
          setSetDialogOpen(o);
          if (!o) {
            setEditSet(null);
            setDuplikatSet(null);
          }
        }}
        editMaterial={editSet ?? undefined}
        duplicateFrom={duplikatSet ?? undefined}
      />

      <AliasDialog
        material={aliasTarget}
        onClose={() => setAliasTarget(null)}
      />
    </div>
  );
  }
}
